import { type CategoryKind, formatEUR } from '@budget/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAccounts,
  useCategories,
  useCreateTransaction,
  useSavings,
  useSplitTransaction,
} from '../api/hooks';
import { NumericPad } from '../components/NumericPad';
import { SplitModal } from '../components/SplitModal';

/** Date du jour vue par l'utilisateur, pas en UTC : à 23 h, ce n'est pas pareil. */
function localToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function EntryScreen() {
  const navigate = useNavigate();
  const accounts = useAccounts();
  const categories = useCategories();
  const create = useCreateTransaction();
  const split = useSplitTransaction();

  const savings = useSavings();

  const [kind, setKind] = useState<CategoryKind>('expense');
  const [cents, setCents] = useState(0);
  const [step, setStep] = useState<'amount' | 'category'>('amount');
  const [fundedByGoalId, setFundedByGoalId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<string[]>([]);
  const [uncategorised, setUncategorised] = useState(false);
  const [splitting, setSplitting] = useState(false);

  const account = (accounts.data ?? []).find((item) => item.type === 'checking');
  const choices = (categories.data ?? []).filter((category) => category.kind === kind);
  // Seuls les pôles qui contiennent assez peuvent financer cette dépense.
  const fundable = (savings.data?.goals ?? []).filter((goal) => goal.savedCents >= cents);

  // Dans l'ordre de la grille, pour que la modale de ventilation présente les
  // catégories comme l'utilisateur vient de les voir.
  const selectedCategories = choices.filter((category) => selected.includes(category.id));
  const busy = create.isPending || split.isPending;

  function toggle(categoryId: string) {
    setUncategorised(false);
    setSelected((current) =>
      current.includes(categoryId)
        ? current.filter((id) => id !== categoryId)
        : [...current, categoryId],
    );
  }

  function pushDigits(digits: string) {
    setCents((current) => {
      const next = Number(`${current}${digits}`);
      // Plafond volontaire : au-delà, c'est une faute de frappe.
      return next > 99_999_999 ? current : next;
    });
  }

  async function record(categoryId: string | null) {
    if (account === undefined) {
      setError('Aucun compte courant : créez-en un d’abord.');
      return;
    }
    try {
      await create.mutateAsync(
        kind === 'expense'
          ? {
              type: 'expense',
              accountId: account.id,
              amountCents: cents,
              occurredOn: localToday(),
              categoryId,
              fundedByGoalId,
            }
          : {
              type: 'income',
              accountId: account.id,
              amountCents: cents,
              occurredOn: localToday(),
              categoryId,
            },
      );
      void navigate('/');
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : 'Enregistrement impossible. Réessayez.',
      );
    }
  }

  /** Une seule catégorie enregistre directement ; plusieurs ouvrent la ventilation. */
  async function submit() {
    setError(null);
    if (uncategorised) return record(null);
    if (selected.length === 1) return record(selected[0]!);
    if (selected.length > 1) setSplitting(true);
  }

  async function confirmSplit(parts: number[]) {
    if (account === undefined) {
      setError('Aucun compte courant : créez-en un d’abord.');
      return;
    }
    setError(null);
    try {
      await split.mutateAsync({
        accountId: account.id,
        totalCents: cents,
        occurredOn: localToday(),
        parts: selectedCategories.map((category, index) => ({
          categoryId: category.id,
          amountCents: parts[index] ?? 0,
        })),
      });
      void navigate('/');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Ventilation impossible. Réessayez.');
    }
  }

  return (
    <div className="flex min-h-dvh flex-col px-5 pt-4">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => (step === 'amount' ? void navigate('/') : setStep('amount'))}
          className="rounded-lg px-2 py-1 text-sm text-ink-secondary"
        >
          ← {step === 'amount' ? 'Annuler' : 'Montant'}
        </button>

        {step === 'amount' && (
          <div className="flex rounded-lg border border-line p-0.5 text-sm">
            {(['expense', 'income'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setKind(option)}
                className={`rounded-md px-3 py-1 ${
                  kind === option ? 'bg-series text-white' : 'text-ink-secondary'
                }`}
              >
                {option === 'expense' ? 'Dépense' : 'Revenu'}
              </button>
            ))}
          </div>
        )}
      </div>

      <p
        className="mt-8 text-center text-5xl font-semibold tracking-tight"
        aria-live="polite"
        aria-label={`Montant : ${formatEUR(cents)}`}
      >
        {formatEUR(cents)}
      </p>

      {error !== null && (
        <p role="alert" className="mt-3 text-center text-sm text-critical">
          {error}
        </p>
      )}

      {step === 'amount' ? (
        <div className="safe-bottom mt-auto flex flex-col gap-3 pb-4">
          <NumericPad
            onDigit={pushDigits}
            onBackspace={() => setCents((current) => Math.floor(current / 10))}
          />
          <button
            type="button"
            disabled={cents === 0}
            onClick={() => setStep('category')}
            className="rounded-2xl bg-series py-4 text-base font-medium text-white disabled:opacity-40"
          >
            Suivant
          </button>
        </div>
      ) : (
        <div className="safe-bottom mt-6 flex flex-col gap-3 pb-4">
          {kind === 'expense' && fundable.length > 0 && (
            <label className="flex flex-col gap-1.5 rounded-2xl border border-line bg-surface p-3">
              <span className="text-sm text-ink-secondary">Financée par un pôle ?</span>
              <select
                value={fundedByGoalId ?? ''}
                onChange={(event) => setFundedByGoalId(event.target.value || null)}
                className="rounded-xl border border-line bg-plane px-3 py-2 text-base"
              >
                <option value="">Non — sur le budget du mois</option>
                {fundable.map((goal) => (
                  <option key={goal.id} value={goal.id}>
                    {goal.name} ({formatEUR(goal.savedCents, { round: true })} disponibles)
                  </option>
                ))}
              </select>
              {fundedByGoalId !== null && (
                <span className="text-xs text-ink-muted">
                  Cette dépense ne pèsera pas sur le reste à vivre : le pôle la couvre.
                </span>
              )}
            </label>
          )}

          <p className="text-sm text-ink-secondary">
            {selected.length > 1
              ? `${selected.length} catégories — la dépense sera ventilée`
              : 'Dans quelle catégorie ?'}
          </p>

          <div className="grid grid-cols-4 gap-2">
            {choices.map((category) => (
              <CategoryTile
                key={category.id}
                glyph={category.icon}
                name={category.name}
                selected={selected.includes(category.id)}
                onClick={() => toggle(category.id)}
              />
            ))}

            {/* « Sans catégorie » prend sa place dans la grille plutôt qu'une
                ligne à part : seize tuiles remplissent exactement quatre rangées. */}
            <CategoryTile
              glyph="∅"
              name="Sans catégorie"
              selected={uncategorised}
              onClick={() => {
                setUncategorised((value) => !value);
                setSelected([]);
              }}
            />
          </div>

          {(selected.length > 0 || uncategorised) && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              className="rounded-2xl bg-series py-4 text-base font-medium text-white disabled:opacity-40"
            >
              {selected.length > 1
                ? 'Ventiler entre les catégories'
                : 'Enregistrer la transaction'}
            </button>
          )}
        </div>
      )}

      {splitting && (
        <SplitModal
          totalCents={cents}
          categories={selectedCategories}
          pending={busy}
          error={error}
          onConfirm={(parts) => void confirmSplit(parts)}
          onClose={() => setSplitting(false)}
        />
      )}
    </div>
  );
}

/** Tuile de la grille : un pictogramme, un nom, un état de sélection. */
function CategoryTile({
  glyph,
  name,
  selected,
  onClick,
}: {
  glyph: string;
  name: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex flex-col items-center gap-1 rounded-2xl border px-1 py-3 active:bg-line ${
        selected ? 'border-series bg-plane' : 'border-transparent bg-surface'
      }`}
    >
      <span className="text-2xl" aria-hidden="true">
        {glyph}
      </span>
      <span className="text-center text-[11px] leading-tight text-ink-secondary">{name}</span>
    </button>
  );
}
